/**
 * Minimal subset of i18next's TFunction we actually rely on.
 * Accepting this looser shape lets us pass either the bare i18next `t`
 * (`(key: string) => string`) or the typed `useTranslation().t` (TFunction)
 * without TypeScript complaints in callers.
 */
type TLike = (key: string) => string;

/** An UPPER_SNAKE identifier (RETURN_APPROVED, NO_WE) — a system code, not prose. */
const INTERNAL_CODE = /\b[A-Z][A-Z0-9]*_[A-Z0-9_]+\b/;

/**
 * Translate a carrier-supplied message code (e.g. "NO_WE", "DENIED",
 * "NO_ANSWER") into the merchant's chosen language.
 *
 * Why this exists:
 *   Some carriers (currently Prime) push short uppercase codes as the order's
 *   `carrier_message`. Showing those codes in the merchant dashboard is a
 *   poor experience. We translate them at the render layer using the
 *   `carrier_codes` namespace in the i18n translation files.
 *
 * Safety guarantees:
 *   - Returns free-form Arabic/English text from other carriers
 *     (Hi-Express, Zaeem, etc.) unchanged when it is not in our table.
 *   - Returns '' for an untranslated internal code (BOXY-587).
 *   - Returns the raw `---` placeholder unchanged.
 *   - Returns null/undefined input as `''` (safe for direct rendering).
 *
 * Usage:
 *   const { t } = useTranslation();
 *   const display = translateCarrierMessage(order.carrier_message, t);
 */
export const translateCarrierMessage = (raw: string | null | undefined, t: TLike): string => {
  if (raw === null || raw === undefined) return '';
  const trimmed = String(raw).trim();
  if (!trimmed || trimmed === '---') return raw ?? '';

  const key = `carrier_codes.${trimmed}`;
  const translated = t(key);

  if (translated !== key) return translated;

  // BOXY-587 — an untranslated INTERNAL code is not information for a
  // merchant: "'RETURN_APPROVED' is not allowed for current shipment status"
  // is a carrier API error. Show nothing rather than the raw code.
  if (INTERNAL_CODE.test(trimmed)) return '';

  // i18next returns the key itself when a translation is missing.
  // Free-form carrier text falls back to the raw value.
  return raw;
};
